// FirmwareService: Download Firmware von API
import axios from 'axios';

export const URL_API = 'https://api.spinnax.com';

/**
 * Download a firmware file from the API (auth required)
 * @param major Firmware major version (number or string)
 * @param minor Firmware minor version (number or string)
 * @param patch Firmware patch version (number or string)
 * @param token Firebase Auth Bearer Token
 * @returns Promise<Blob> mit der Firmwaredatei
 */
export async function downloadFirmware(
  major: number | string,
  minor: number | string,
  patch: number | string,
  token: string
): Promise<Blob> {
  const url = `${URL_API}/firmware/download/${major}/${minor}/${patch}`;
  const response = await axios.get(url, {
    responseType: 'blob',
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });
  if (response.status !== 200) {
    throw new Error(`Failed to download firmware: ${response.status} ${response.statusText}`);
  }
  return response.data as Blob;
}

/**
 * Download the newest beta firmware file from the API (auth required)
 * @param token Firebase Auth Bearer Token
 * @returns Promise<Blob> mit der Firmwaredatei
 */
export async function downloadFirmwareBeta(token: string): Promise<Blob> {
  const url = `${URL_API}/firmware/download/beta`;
  const response = await axios.get(url, {
    responseType: 'blob',
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });
  if (response.status !== 200) {
    throw new Error(`Failed to download beta firmware: ${response.status} ${response.statusText}`);
  }
  return response.data as Blob;
}

/**
 * Download the newest production firmware file from the API (auth required)
 * @param token Firebase Auth Bearer Token
 * @returns Promise<{ blob: Blob, filename: string }> mit der Firmwaredatei und Dateiname
 */
export async function downloadFirmwareProduction(token: string): Promise<{ blob: Blob, filename: string }> {
  const url = `${URL_API}/firmware/download/production`;
  const response = await axios.get(url, {
    responseType: 'blob',
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });
  if (response.status !== 200) {
    throw new Error(`Failed to download production firmware: ${response.status} ${response.statusText}`);
  }
  // Dateiname aus content-disposition Header extrahieren
  let filename = 'firmware.bin';
  const disposition = response.headers['content-disposition'];
  if (disposition) {
    const match = disposition.match(/filename="?([^";]+)"?/);
    if (match) filename = match[1];
  }
  return { blob: response.data as Blob, filename };
}

// Optional: Helper für base64
export async function downloadFirmwareBase64(
  major: number | string,
  minor: number | string,
  patch: number | string,
  token: string
): Promise<string> {
  const blob = await downloadFirmware(major, minor, patch, token);
  return await blobToBase64(blob);
}

// Helper: Blob zu base64
export function blobToBase64(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => {
      if (typeof reader.result === 'string') {
        // Entferne data:...;base64, Präfix falls vorhanden
        const base64 = reader.result.split(',')[1] || reader.result;
        resolve(base64);
      } else {
        reject(new Error('Failed to convert blob to base64'));
      }
    };
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
}
